import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  ChefHat, Clock, Users, Flame, Heart, Bookmark, BookmarkCheck,
  Search, Filter, TrendingUp, Star, ArrowRight, Plus,
  Utensils, Leaf, Fish, Beef, Cake, Coffee, Soup, Flag, MoreVertical, Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ReportDialog from '../components/moderation/ReportDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recipe categories with icons
const CATEGORIES = [
  { id: 'all', label: 'Toutes', labelEn: 'All', icon: Utensils },
  { id: 'plats', label: 'Plats', labelEn: 'Main Dishes', icon: Beef },
  { id: 'entrees', label: 'Entrées', labelEn: 'Starters', icon: Soup },
  { id: 'desserts', label: 'Desserts', labelEn: 'Desserts', icon: Cake },
  { id: 'boissons', label: 'Boissons', labelEn: 'Drinks', icon: Coffee },
  { id: 'vegetarien', label: 'Végétarien', labelEn: 'Vegetarian', icon: Leaf },
  { id: 'poisson', label: 'Poisson', labelEn: 'Fish', icon: Fish },
];

// Difficulty colors
const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
};

const DIFFICULTY_LABELS = {
  easy: { fr: 'Facile', en: 'Easy' },
  medium: { fr: 'Moyen', en: 'Medium' },
  hard: { fr: 'Difficile', en: 'Hard' },
};

export default function RecipesPage() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  
  // State
  const [recipes, setRecipes] = useState([]);
  const [popularRecipes, setPopularRecipes] = useState([]);
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [savedRecipes, setSavedRecipes] = useState(new Set());
  const [reportTarget, setReportTarget] = useState(null);
  
  // Fetch recipes
  const fetchRecipes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      
      const res = await fetch(`${API_URL}/api/recipes?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
      }
    } catch (err) {
      console.error('Failed to fetch recipes:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory]);
  
  // Fetch popular recipes
  const fetchPopularRecipes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/recipes/popular?limit=6`);
      if (res.ok) {
        const data = await res.json();
        setPopularRecipes(data.recipes || []);
      }
    } catch (err) {
      console.error('Failed to fetch popular recipes:', err);
    }
  }, []);
  
  // Fetch recent recipes
  const fetchRecentRecipes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/recipes/recent?limit=8`);
      if (res.ok) {
        const data = await res.json();
        setRecentRecipes(data.recipes || []);
      }
    } catch (err) {
      console.error('Failed to fetch recent recipes:', err);
    }
  }, []);
  
  // Fetch saved recipes
  const fetchSavedRecipes = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/recipes/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedRecipes(new Set((data.recipes || []).map(r => r.id)));
      }
    } catch (err) {
      console.error('Failed to fetch saved recipes:', err);
    }
  }, [token]);
  
  // Toggle save recipe
  const toggleSaveRecipe = async (recipeId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!token) {
      toast.error(t('auth.loginRequired', 'Please login to save recipes'));
      navigate('/auth');
      return;
    }
    
    const isSaved = savedRecipes.has(recipeId);
    
    try {
      const res = await fetch(`${API_URL}/api/recipes/${recipeId}/${isSaved ? 'unsave' : 'save'}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSavedRecipes(prev => {
          const next = new Set(prev);
          if (isSaved) {
            next.delete(recipeId);
            toast.success(t('recipes.removed', 'Recipe removed from saved'));
          } else {
            next.add(recipeId);
            toast.success(t('recipes.saved', 'Recipe saved!'));
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to toggle save:', err);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchRecipes();
    fetchPopularRecipes();
    fetchRecentRecipes();
    fetchSavedRecipes();
  }, [fetchRecipes, fetchPopularRecipes, fetchRecentRecipes, fetchSavedRecipes]);
  
  // Search effect
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchRecipes();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedCategory, fetchRecipes]);
  
  // Recipe Card Component
  const RecipeCard = ({ recipe, size = 'normal' }) => {
    const isSaved = savedRecipes.has(recipe.id);
    const isLarge = size === 'large';
    
    return (
      <Link 
        to={`/recipe/${recipe.id}`}
        className="group block"
        data-testid={`recipe-card-${recipe.id}`}
      >
        <Card className={`overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 ${isLarge ? 'h-full' : ''}`}>
          {/* Image */}
          <div className={`relative overflow-hidden ${isLarge ? 'aspect-[4/3]' : 'aspect-video'}`}>
            {recipe.image_url ? (
              <img 
                src={recipe.image_url} 
                alt={recipe.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
                <ChefHat className="w-12 h-12 text-orange-300" />
              </div>
            )}
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Save button */}
            <div className="absolute top-3 right-3 flex gap-1">
              <button
                onClick={(e) => toggleSaveRecipe(recipe.id, e)}
                className="p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
              >
                {isSaved ? (
                  <BookmarkCheck className="w-5 h-5 text-orange-500" />
                ) : (
                  <Bookmark className="w-5 h-5 text-gray-600" />
                )}
              </button>
              {token && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.preventDefault()}
                      className="p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.preventDefault();
                        setReportTarget({ type: 'recipe', id: recipe.id, name: recipe.title });
                      }}
                      className="text-red-600"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Signaler
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* Difficulty badge */}
            <div className="absolute top-3 left-3">
              <Badge className={`${DIFFICULTY_COLORS[recipe.difficulty] || DIFFICULTY_COLORS.medium} font-medium`}>
                {DIFFICULTY_LABELS[recipe.difficulty]?.[lang] || recipe.difficulty}
              </Badge>
            </div>
            
            {/* Time & servings */}
            <div className="absolute bottom-3 left-3 flex items-center gap-3 text-white text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {recipe.total_time || recipe.prep_time || '30'} min
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {recipe.servings || 4}
              </span>
            </div>
          </div>
          
          {/* Content */}
          <CardContent className="p-4">
            <h3 className={`font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2 ${isLarge ? 'text-xl mb-2' : 'text-base mb-1'}`}>
              {recipe.title}
            </h3>
            
            {isLarge && recipe.description && (
              <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                {recipe.description}
              </p>
            )}
            
            {/* Author */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={recipe.author?.profile_picture} />
                  <AvatarFallback className="text-xs bg-orange-100 text-orange-600">
                    {recipe.author?.display_name?.[0] || 'C'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-gray-500">
                  {recipe.author?.display_name || 'Chef'}
                </span>
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  {recipe.likes_count || 0}
                </span>
                {recipe.rating > 0 && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    {recipe.rating?.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };
  
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="recipes-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-500 via-red-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <Badge className="bg-white/20 text-white mb-4">
                <ChefHat className="w-3 h-3 mr-1" />
                {t('recipes.community', 'Community Recipes')}
              </Badge>
              <h1 className="font-fraunces text-4xl md:text-5xl font-bold mb-3">
                {t('recipes.discoverTitle', 'Discover Tunisian Recipes')}
              </h1>
              <p className="text-white/80 text-lg max-w-xl">
                {t('recipes.discoverSubtitle', 'Explore authentic recipes shared by our community of passionate cooks')}
              </p>
            </div>
            
            <div className="flex flex-col items-stretch md:items-end gap-3">
              {token && (
                <Button
                  size="lg"
                  className="w-full bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8"
                  onClick={() => navigate('/create')}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('recipes.shareRecipe', 'Share a Recipe')}
                </Button>
              )}
              <Link to="/chef-hedi" className="lg:hidden w-full">
                <Button
                  size="lg"
                  className="w-full bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-full px-8 backdrop-blur-sm transition-all"
                >
                  <Bot className="w-5 h-5 mr-2" />
                  Chef Mehdi
                  <span className="ml-2 bg-white/25 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">AI</span>
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder={t('recipes.searchPlaceholder', 'Search for a recipe, ingredient...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg rounded-full bg-white text-gray-900 border-0 shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Categories */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'ghost'}
                  className={`rounded-full whitespace-nowrap ${
                    selectedCategory === cat.id 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                      : 'hover:bg-orange-50'
                  }`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {lang === 'en' ? cat.labelEn : cat.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Popular Recipes Section */}
        {selectedCategory === 'all' && !searchQuery && popularRecipes.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('recipes.popularTitle', 'Popular Recipes')}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    {t('recipes.popularSubtitle', 'Most loved by our community')}
                  </p>
                </div>
              </div>
              <Button variant="ghost" className="text-orange-600 hover:text-orange-700">
                {t('common.seeAll', 'See all')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularRecipes.slice(0, 3).map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} size="large" />
              ))}
            </div>
          </section>
        )}
        
        {/* Recent Recipes Section */}
        {selectedCategory === 'all' && !searchQuery && recentRecipes.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('recipes.recentTitle', 'Recent Recipes')}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    {t('recipes.recentSubtitle', 'Fresh from our creators')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recentRecipes.map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          </section>
        )}
        
        {/* All Recipes / Search Results */}
        <section>
          {(searchQuery || selectedCategory !== 'all') && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {searchQuery 
                  ? t('recipes.searchResults', 'Search Results')
                  : CATEGORIES.find(c => c.id === selectedCategory)?.[lang === 'en' ? 'labelEn' : 'label']
                }
              </h2>
              <span className="text-gray-500">
                {recipes.length} {t('recipes.recipesFound', 'recipes')}
              </span>
            </div>
          )}
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1,2,3,4,5,6,7,8].map(i => (
                <Card key={i} className="overflow-hidden">
                  <div className="aspect-video bg-gray-200 animate-pulse" />
                  <CardContent className="p-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-16">
              <ChefHat className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {t('recipes.noRecipes', 'No recipes found')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('recipes.tryDifferent', 'Try a different search or category')}
              </p>
              {token && (
                <Button onClick={() => navigate('/create')} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('recipes.beFirst', 'Be the first to share!')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recipes.map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          )}
        </section>
      </div>
      
      {/* Report Dialog */}
      {reportTarget && (
        <ReportDialog
          open={!!reportTarget}
          onOpenChange={(open) => !open && setReportTarget(null)}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetName={reportTarget.name}
        />
      )}
    </div>
  );
}
